import React, { useCallback, useMemo, useState } from 'react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import Badge from '../components/ui/Badge';
import AssignmentService from '../services/AssignmentService';
import ExternalMessageService from '../services/ExternalMessageService';
import StaffService from '../services/StaffService';
import { useStudentPortal } from '../store/StudentPortalContext';
import { useSettings } from '../store/SettingsContext';
import { formatDate, formatDateTime } from '../utils/dateFormat';

const buildFileMeta = (file) => ({
  name: file.name,
  type: file.type || 'file',
  size: `${Math.round(file.size / 1024)} KB`,
  fileUrl: ''
});

const readFileAsDataUrl = (file) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve({ ...buildFileMeta(file), fileUrl: reader.result?.toString() || '' });
    };
    reader.onerror = () => {
      resolve({ ...buildFileMeta(file), fileUrl: '' });
    };
    reader.readAsDataURL(file);
  });

const readFiles = async (fileList) => {
  const files = Array.from(fileList || []);
  if (files.length === 0) return [];
  return Promise.all(files.map((file) => readFileAsDataUrl(file)));
};

const getFileExtension = (file) => {
  if (!file?.name) return '';
  const parts = file.name.split('.');
  return parts.length > 1 ? parts.pop().toLowerCase() : '';
};

const isZipFile = (file) => {
  const ext = getFileExtension(file);
  return ext === 'zip' || file.type === 'application/zip';
};

const getFileUrl = (file) => file?.fileUrl || file?.url || file?.link || '';

const openFileInNewWindow = (file) => {
  const url = getFileUrl(file);
  if (!url) return;
  window.open(url, '_blank', 'noopener,noreferrer');
};

const downloadFile = (file) => {
  const url = getFileUrl(file);
  if (!url) return;
  const link = document.createElement('a');
  link.href = url;
  link.download = file.name || 'download';
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
};

const confirmDelete = (label) => {
  const response = window.prompt(`Type YES to delete ${label}. This only removes it from SafeguardPro.`);
  return response && response.trim().toLowerCase() === 'yes';
};

export default function StudentPortalAssignmentsPage() {
  const { student } = useStudentPortal();
  const { settings } = useSettings();
  const assignmentService = useMemo(() => new AssignmentService(), []);
  const messageService = useMemo(() => new ExternalMessageService(), []);
  const staffService = useMemo(() => new StaffService(), []);
  const [activeAssignment, setActiveAssignment] = useState(null);
  const [submissionOpen, setSubmissionOpen] = useState(false);
  const [submissionNotes, setSubmissionNotes] = useState('');
  const [submissionFiles, setSubmissionFiles] = useState([]);
  const [, setRefreshTick] = useState(0);
  const [assignmentTab, setAssignmentTab] = useState('active');

  const assignments = student ? assignmentService.listForStudents([student.id]) : [];
  const assignmentPoints = Number(settings?.studentPortal?.pointsRules?.assignmentSubmission || 0);

  const getSubmissionStatus = useCallback((assignment) => {
    if (!student) return { status: 'pending', submittedAt: null };
    const submissions = (assignment.timeline || [])
      .filter((entry) => entry.type === 'submission' && entry.studentId === student.id)
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    if (submissions.length === 0) return { status: 'pending', submittedAt: null };
    return { status: 'submitted', submittedAt: submissions[0].createdAt };
  }, [student]);

  const getReviewStatus = useCallback((assignment) => {
    if (!student) return { status: '', dueDate: '' };
    const reviews = (assignment.timeline || [])
      .filter((entry) => entry.type === 'review' && entry.studentId === student.id)
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    if (reviews.length === 0) return { status: '', dueDate: '' };
    return {
      status: reviews[0].reviewStatus || '',
      dueDate: reviews[0].reviewDueDate || ''
    };
  }, [student]);

  const completedAssignments = assignments.filter((assignment) => {
    const review = getReviewStatus(assignment);
    return review.status === 'approved';
  });
  const activeAssignments = assignments.filter((assignment) => {
    const review = getReviewStatus(assignment);
    return review.status !== 'approved';
  });

  const assignmentStats = useMemo(() => {
    const now = new Date();
    const stats = {
      active: activeAssignments.length,
      completed: completedAssignments.length,
      overdue: 0,
      resubmit: 0
    };
    activeAssignments.forEach((assignment) => {
      const submission = getSubmissionStatus(assignment);
      if (assignment.dueDate && new Date(assignment.dueDate) < now && submission.status !== 'submitted') {
        stats.overdue += 1;
      }
      const review = getReviewStatus(assignment);
      if (review.status === 'resubmit') {
        stats.resubmit += 1;
      }
    });
    return stats;
  }, [activeAssignments, completedAssignments, getReviewStatus, getSubmissionStatus]);

  const handleSubmit = () => {
    if (!student || !activeAssignment) return;
    assignmentService.addTimelineEntry(activeAssignment.id, {
      type: 'submission',
      message: submissionNotes || 'Submission uploaded.',
      createdBy: student.name,
      studentId: student.id,
      attachments: submissionFiles
    });

    const staffRecipients = [];
    if (activeAssignment.createdById) {
      const staff = staffService.list().find((member) => member.id === activeAssignment.createdById);
      if (staff) staffRecipients.push({ id: staff.id, name: staff.name, type: 'staff' });
    }
    if (staffRecipients.length === 0 && student.keyWorker?.id) {
      staffRecipients.push({ id: student.keyWorker.id, name: student.keyWorker.name, type: 'staff' });
    }
    if (staffRecipients.length > 0) {
      messageService.logStudentMessage({
        subject: `Submission: ${activeAssignment.title}`,
        body: `${student.name} submitted work for ${activeAssignment.title}.`,
        student,
        staffRecipients
      });
    }

    setSubmissionOpen(false);
    setSubmissionNotes('');
    setSubmissionFiles([]);
    setRefreshTick((prev) => prev + 1);
  };

  const refreshAssignments = () => {
    setRefreshTick((prev) => prev + 1);
  };

  const handleDeleteAssignmentAttachment = (index) => {
    if (!activeAssignment) return;
    const file = activeAssignment.attachments?.[index];
    if (!file) return;
    if (!confirmDelete(file.name || 'this file')) return;
    const nextAttachments = activeAssignment.attachments.filter((_, idx) => idx !== index);
    assignmentService.update(activeAssignment.id, { attachments: nextAttachments });
    setActiveAssignment(assignmentService.getById(activeAssignment.id));
    refreshAssignments();
  };

  const handleDeleteTimelineAttachment = (entryId, index) => {
    if (!activeAssignment) return;
    const entry = activeAssignment.timeline.find((item) => item.id === entryId);
    if (!entry) return;
    const file = entry.attachments?.[index];
    if (!file) return;
    if (!confirmDelete(file.name || 'this file')) return;
    const nextTimeline = activeAssignment.timeline.map((item) => {
      if (item.id !== entryId) return item;
      return {
        ...item,
        attachments: (item.attachments || []).filter((_, idx) => idx !== index)
      };
    });
    assignmentService.update(activeAssignment.id, { timeline: nextTimeline });
    setActiveAssignment(assignmentService.getById(activeAssignment.id));
    refreshAssignments();
  };

  const renderAttachmentRow = (file, index, onDelete) => {
    const zipOnly = isZipFile(file);
    const url = getFileUrl(file);
    const canPreview = Boolean(url) && !zipOnly;
    return (
      <div key={`${file.name}-${index}`} className="sp-panel sp-panel--muted p-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="text-sm">{file.name}</div>
          <div className="flex items-center gap-2">
            {canPreview && (
              <Button size="sm" variant="secondary" type="button" onClick={() => openFileInNewWindow(file)}>
                View
              </Button>
            )}
            <Button size="sm" variant="ghost" type="button" onClick={() => downloadFile(file)} disabled={!url}>
              Download
            </Button>
            {onDelete && (
              <Button size="sm" variant="ghost" type="button" onClick={onDelete}>
                Delete
              </Button>
            )}
          </div>
        </div>
        {zipOnly && <div className="text-xs sp-muted mt-1">Zip file - download only.</div>}
        {!url && <div className="text-xs sp-muted mt-1">No file data available.</div>}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="sp-value" style={{ fontSize: '20px', fontWeight: 600 }}>Assignments</h2>
        <p className="sp-muted">Track your assignments and upload submissions.</p>
      </div>

      <div className="grid lg:grid-cols-4 gap-3">
        <Card className="p-4">
          <div className="text-xs sp-muted">Active</div>
          <div className="sp-value" style={{ fontWeight: 600, fontSize: '20px' }}>{assignmentStats.active}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs sp-muted">Completed</div>
          <div className="sp-value" style={{ fontWeight: 600, fontSize: '20px' }}>{assignmentStats.completed}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs sp-muted">Overdue</div>
          <div className="sp-value" style={{ fontWeight: 600, fontSize: '20px' }}>{assignmentStats.overdue}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs sp-muted">Resubmits</div>
          <div className="sp-value" style={{ fontWeight: 600, fontSize: '20px' }}>{assignmentStats.resubmit}</div>
        </Card>
      </div>

      <div className="grid lg:grid-cols-[1.1fr_1fr] gap-4">
        <Card className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant={assignmentTab === 'active' ? 'secondary' : 'ghost'}
              onClick={() => setAssignmentTab('active')}
            >
              Active
            </Button>
            <Button
              size="sm"
              variant={assignmentTab === 'completed' ? 'secondary' : 'ghost'}
              onClick={() => setAssignmentTab('completed')}
            >
              Completed
            </Button>
          </div>
          {(assignmentTab === 'active' ? activeAssignments : completedAssignments).length === 0 ? (
            <div className="sp-panel sp-panel--muted p-3 text-sm">No assignments yet.</div>
          ) : (
            (assignmentTab === 'active' ? activeAssignments : completedAssignments).map((assignment) => {
              const submission = getSubmissionStatus(assignment);
              const review = getReviewStatus(assignment);
              const isOverdue =
                assignment.dueDate && new Date(assignment.dueDate) < new Date() && submission.status !== 'submitted';
              return (
                <button
                  key={assignment.id}
                  type="button"
                  className={`sp-panel w-full p-3 text-left ${activeAssignment?.id === assignment.id ? '' : 'sp-panel--muted'} ${
                    isOverdue ? 'sp-panel--critical' : ''
                  }`}
                  onClick={() => setActiveAssignment(assignment)}
                >
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="sp-value" style={{ fontWeight: 600 }}>{assignment.title}</div>
                    <div className="flex items-center gap-2">
                      <Badge variant={submission.status === 'submitted' ? 'success' : isOverdue ? 'danger' : 'warning'}>
                        {submission.status === 'submitted' ? 'Submitted' : isOverdue ? 'Overdue' : 'Pending'}
                      </Badge>
                      {review.status && (
                        <Badge variant={review.status === 'approved' ? 'success' : review.status === 'resubmit' ? 'review' : review.status === 'failed' ? 'danger' : 'inactive'}>
                          {review.status}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="text-xs sp-muted">
                    Due {assignment.dueDate ? formatDate(assignment.dueDate) : 'No due date'}
                  </div>
                  {assignmentTab === 'active' && assignmentPoints > 0 && (
                    <div className="text-xs sp-muted">Earn {assignmentPoints} pts on approval</div>
                  )}
                  {submission.submittedAt && (
                    <div className="text-xs sp-muted">Submitted {formatDateTime(submission.submittedAt)}</div>
                  )}
                  {review.status === 'resubmit' && review.dueDate && (
                    <div className="text-xs sp-warning-text">Resubmit by {formatDate(review.dueDate)}</div>
                  )}
                </button>
              );
            })
          )}
        </Card>

        <Card className="p-4 space-y-3">
          {!activeAssignment ? (
            <div className="text-sm sp-muted">Select an assignment to view details.</div>
          ) : (
            <>
              {(() => {
                const submission = getSubmissionStatus(activeAssignment);
                const review = getReviewStatus(activeAssignment);
                return (
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant={submission.status === 'submitted' ? 'success' : 'warning'}>
                      {submission.status === 'submitted' ? 'Submitted' : 'Pending'}
                    </Badge>
                    {review.status && (
                      <Badge variant={review.status === 'approved' ? 'success' : review.status === 'resubmit' ? 'review' : review.status === 'failed' ? 'danger' : 'inactive'}>
                        {review.status}
                      </Badge>
                    )}
                    {submission.submittedAt && (
                      <span className="text-xs sp-muted">Submitted {formatDateTime(submission.submittedAt)}</span>
                    )}
                    {review.status === 'resubmit' && review.dueDate && (
                      <span className="text-xs sp-warning-text">Resubmit by {formatDate(review.dueDate)}</span>
                    )}
                  </div>
                );
              })()}
              <div>
                <div className="sp-value" style={{ fontWeight: 600 }}>{activeAssignment.title}</div>
                <div className="text-xs sp-muted">
                  Due {activeAssignment.dueDate ? formatDate(activeAssignment.dueDate) : 'No due date'}
                </div>
                {assignmentPoints > 0 && getReviewStatus(activeAssignment).status !== 'approved' && (
                  <div className="text-xs sp-muted">Complete to earn {assignmentPoints} pts</div>
                )}
              </div>
              <div className="text-sm">{activeAssignment.description || 'No description provided.'}</div>
              {activeAssignment.attachments?.length > 0 && (
                <div className="sp-panel sp-panel--muted p-3 text-xs space-y-2">
                  <div className="sp-label">Assignment resources</div>
                  {activeAssignment.attachments.map((file, idx) =>
                    renderAttachmentRow(file, idx, () => handleDeleteAssignmentAttachment(idx))
                  )}
                </div>
              )}
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={() => setSubmissionOpen(true)}>Upload submission</Button>
              </div>
              <div>
                <div className="sp-label">Timeline</div>
                <div className="space-y-2 mt-2">
                  {activeAssignment.timeline
                    .slice()
                    .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
                    .map((entry) => (
                      <div key={entry.id} className="sp-panel sp-panel--muted p-3 text-sm">
                        <div className="flex items-center justify-between gap-2">
                          <div className="sp-value" style={{ fontWeight: 600 }}>
                            {entry.type === 'submission' ? 'Submission' : 'Update'}
                          </div>
                          <div className="text-xs sp-muted">{formatDateTime(entry.createdAt)}</div>
                        </div>
                        <div className="text-xs sp-muted">By {entry.createdBy}</div>
                        {entry.message && <div className="mt-2">{entry.message}</div>}
                        {entry.attachments?.length > 0 && (
                          <div className="mt-2 space-y-2">
                            {entry.attachments.map((file, idx) =>
                              renderAttachmentRow(file, idx, () => handleDeleteTimelineAttachment(entry.id, idx))
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              </div>
            </>
          )}
        </Card>
      </div>

      <Modal isOpen={submissionOpen} onClose={() => setSubmissionOpen(false)}>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Upload submission</h3>
              <p className="text-sm sp-muted">Add your files and notes.</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setSubmissionOpen(false)}>Close</Button>
          </div>
          <div>
            <label className="sp-label">Notes</label>
            <textarea
              className="sp-textarea mt-2 w-full"
              rows={3}
              value={submissionNotes}
              onChange={(event) => setSubmissionNotes(event.target.value)}
            />
          </div>
          <div>
            <label className="sp-label">Files</label>
            <input
              type="file"
              multiple
              className="sp-input mt-2 w-full"
              onChange={async (event) => {
                const files = await readFiles(event.target.files);
                setSubmissionFiles(files);
              }}
            />
            {submissionFiles.length > 0 && (
              <div className="text-xs sp-muted mt-2">
                {submissionFiles.map((file) => file.name).join(', ')}
              </div>
            )}
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button variant="secondary" onClick={() => setSubmissionOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={!activeAssignment}>
              Submit
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
